// @ts-nocheck: converted from JS

import Loading from '@/components/Loading';
import SamplesList from '@/components/Subjects/Show/Views/SamplesList';
import Banner from '@/components/UI/Banner';
import Button from '@/components/UI/Button';
import SearchSelect from '@/components/UI/SearchSelect';
import { toWorkflowDateTime } from '@/components/Workflow/Show/Workflow.utils';
import { _isEmpty, _notNil } from '@/littledash';
import { TeamGlossary } from '@/model/Glosary.model';
import { useApiHook } from '@/support/Hooks/api/useApiHook';
import { useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { WorkflowNoDataCard } from './WorkflowNoDataCard';

const SamplesView = ({
  study,
  subject,
  samples,
  fetchAnimalDataByType,
  openModal,
  closeModal,
  workflow,
  measured_at,
  onSave,
  canWrite,
}) => {
  const dispatch = useDispatch();
  const {
    response: samplesResponse,
    error,
    loading,
  } = useApiHook({
    endpoint: 'GET /api/v1/team-glossary',
    path: undefined,
    query: {
      group: 'samples',
      perPage: -1,
      filterType: 'and',
      'filters[]': 'status|eq|active',
      order: 'asc',
    },
    options: { onError: { toast: false, slug: 'workflow-samples-load' } },
  });
  const sampleTypes: Array<TeamGlossary> = ((samplesResponse?.body as { data: Array<TeamGlossary> })?.data ?? []).map(
    (sampleType) => ({
      ...sampleType,
      id: sampleType.api_id,
    })
  );

  const [selected, setSelected] = useState([...(workflow.preSelectedSampleTypeIds ?? [])]);
  const sampleTypeSections = useMemo(() => {
    const sampleTypeMap = new Map((sampleTypes ?? []).map((sampleType) => [sampleType.api_id, sampleType]));

    const selectedTypes = (selected ?? []).reduce((acc, sampleTypeId) => {
      if (sampleTypeMap.has(sampleTypeId)) {
        acc.push(sampleTypeMap.get(sampleTypeId));
        sampleTypeMap.delete(sampleTypeId);
      }
      return acc;
    }, []);
    return [
      ...(selectedTypes.length > 0 ? [{ title: 'Selected Sample types', items: selectedTypes }] : []),
      { title: 'Sample types', items: Array.from(sampleTypeMap.values()) },
    ];
  }, [selected, sampleTypes]);

  const handleCallback = (editing = false) => {
    closeModal(dispatch);
    onSave();
    if (workflow.onSave !== 'next-subject' || editing) {
      fetchAnimalDataByType(subject, 'samples');
    }
  };

  const handleModalToggle = () => {
    const sampleTypeMap = new Map((sampleTypes ?? []).map((sampleType) => [sampleType.api_id, sampleType]));
    const samples = selected.reduce((acc, s) => {
      const teamGlossary = sampleTypeMap.get(s);
      if (_notNil(teamGlossary)) {
        acc.push({
          ...teamGlossary,
          team_glossary_api_id: teamGlossary.api_id,
        });
      }
      return acc;
    }, []);

    return openModal('ADD_SAMPLE', {
      animalId: subject.id,
      sampleTypes: samples,
      closeModal,
      collectedAt: toWorkflowDateTime(measured_at),
      allowNaming: !(workflow.autoGenerateSampleIds ?? false),
      handleAfterClose: handleCallback,
    });
  };

  const orderedSamples = samples.sort((a, b) => new Date(b.date) - new Date(a.date));

  return (
    <div className={`flex flex-wrap justify-between items-stetch h-100 ${!canWrite || (loading && 'ui__disabled')}`}>
      <div className="w-50 pa4 br b--moon-gray bg-light-gray">
        {error && (
          <Banner critical dismiss={false} className="mw6 mv3">
            <h3 className="f5 normal lh-title pb2">An error has occurred</h3>
            <p className="f6 pb3 lh-copy">
              We are having an issue fetching the team glossary right now, please try again later. If this keeps
              occurring please contact support.
            </p>
            <Button outline critical url={'mailto:support@benchling.com'}>
              Contact support
            </Button>
          </Banner>
        )}
        {loading && <Loading />}
        {sampleTypes?.length > 0 && (
          <>
            <div className="ui-card">
              <SearchSelect selected={selected} setSelected={setSelected} sections={sampleTypeSections} />
            </div>
            <Button
              className="mt3"
              tooltip={_isEmpty(selected) ? 'Select a sample type to continue' : undefined}
              disabled={_isEmpty(selected)}
              onClick={() => handleModalToggle()}
            >
              Collect sample{selected.length > 1 && 's'}
            </Button>
          </>
        )}
        {sampleTypes?.length === 0 && (
          <Banner info className="mw6" dismiss={false}>
            <h3 className="f5 normal basier-med lh-title">No team glossary items have been added</h3>
            <p className="f6 pt2 lh-copy">
              To get started, click on Glossary in the left hand menu and add sample type glossary items.
            </p>
          </Banner>
        )}
      </div>
      <div className="w-50 bg-white">
        <div className="overflow-y-scroll h-100" style={{ maxHeight: '42rem' }}>
          {orderedSamples.length ? (
            <SamplesList
              study={study}
              samples={orderedSamples}
              openModal={openModal}
              closeModal={closeModal}
              subject={subject}
              handleCallback={handleCallback}
            />
          ) : (
            WorkflowNoDataCard
          )}
        </div>
      </div>
    </div>
  );
};

export default SamplesView;
